import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useState } from 'react'
import { ImageOff } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PhotoProps {
    src: string | null | undefined
    alt: string | null | undefined
    sizes: string
    className?: string
    priority?: boolean
}

// Hosts that next.config.ts lets the image optimizer resize. Keep both lists in sync.
const OPTIMIZED_HOST = /\.cdn\.modulify\.website$/i

const canOptimize = (src: string): boolean => {
    if (src.startsWith('/')) return true

    try {
        const url = new URL(src)

        return url.protocol === 'https:' && OPTIMIZED_HOST.test(url.hostname)
    }
    catch {
        return false
    }
}

/*
 * Fills its (relatively positioned) parent. Photos from the site's storage are resized
 * automatically; any other address is shown as is, and a broken one shows a quiet fallback.
 */
export const Photo: FC<PhotoProps> = ({
    src,
    alt,
    sizes,
    className,
    priority = false
}): ReactNode => {
    const [failed, setFailed] = useState(false)

    if (!src || failed) {
        return (
            <div className={cn('absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground', className)}>
                <ImageOff className='size-6' aria-hidden='true' />
                <span className='sr-only'>{alt || 'Photo unavailable'}</span>
            </div>
        )
    }

    return (
        <Image
            src={src}
            alt={alt ?? ''}
            fill
            sizes={sizes}
            preload={priority}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            unoptimized={!canOptimize(src)}
            onError={() => setFailed(true)}
            className={cn('object-cover', className)}
        />
    )
}
